import { useMemo } from 'react';
import { useNavigate } from 'react-router';

import MorePopover from '@/components/ui/MorePopover';
import { useModal } from '@bitstopco/bitstop-theme';

import type { LocationInstalledMachines } from '@/types';

const LocationsDotsMenu = ({
  location_id,
  atms,
  location_name,
  address,
  place_id,
  marketing_status,
}: Pick<
  {
    place_id: string;
    address: string;
    created_at: string;
    atms: LocationInstalledMachines[];
    location_id: number;
    location_name: string;
    marketing_status: string;
  },
  'location_id' | 'atms' | 'address' | 'place_id' | 'location_name' | 'marketing_status'
>) => {
  const { show } = useModal();
  const navigate = useNavigate();

  const { items } = useMemo(() => {
    const hasAtms = atms.length > 0;
    const isPublished = marketing_status === 'published';
    const isUnpublished = marketing_status === 'unpublished';

    const items = [
      { label: 'See details', value: '/location', link: true, disabled: false },
      {
        label: 'Edit details',
        value: marketing_status === 'published' ? 'location-edit-prompt' : 'location-edit',
        disabled: false,
      },
      { label: 'Install machine', value: 'location-install-machine', disabled: false },
      { label: 'Remove machine', value: 'location-remove-machines', disabled: !hasAtms },
      { label: 'Delete location', value: 'location-delete', disabled: false },
      { label: 'Publish location', value: 'location-publish', disabled: isPublished },
      { label: 'Unpublish location', value: 'location-unpublish', disabled: isUnpublished },
    ];

    return { items, itemLabels: items.map(({ label }) => label) };
  }, [atms, marketing_status]);

  const handleClick = (index: number) => {
    const item = items[index];

    // If the item is disabled, do not perform any action
    if (item.disabled) {
      return; // Simply return to prevent navigation or any action
    }

    if (!item?.value) return;

    // Now, only navigate if the item is not disabled
    if (item.link) {
      navigate(`${item.value}/${location_id}`);
    } else {
      const modalData = getModalData(item.value);
      if (modalData) {
        show(item.value, modalData);
      }
    }
  };

  const getModalData = (value: string) => {
    switch (value) {
      case 'location-edit':
      case 'location-edit-prompt':
        return { data: { location_id, address, place_id } };
      case 'location-install-machine':
        return { location_id, location_name };
      case 'location-remove-machines':
        return { data: { location_id, installed_atms: atms, location_name } };
      case 'location-delete':
        return { data: { location_id, location_name, atms } };
      case 'location-publish':
        return { location_name, atms, location_id };
      case 'location-unpublish':
        return {
          location_id,
          atm_id: atms.filter((atm) => atm.atm_id).pop(),
          location_name,
        };
      default:
        return {};
    }
  };

  return <MorePopover onClick={handleClick} items={items} />;
};

export default LocationsDotsMenu;
